import { useEffect, useState, Fragment } from "react";
import {
    HiPlus,
    HiTrash,
    HiPencilAlt,
    HiUsers,
    HiSearch,
    HiX,
    HiChevronUp,
    HiOutlineChartBar
} from "react-icons/hi";
import { Link } from "react-router-dom";
import { ClipboardCopyIcon, CheckIcon } from "@heroicons/react/outline";
import { useDispatch, useSelector } from "react-redux";
import {
    addMembersAction,
    adjustRewardDistributionAction,
    createGroupAction,
    removeMembersAction,
    renameGroupAction,
    deleteGroupAction
} from "../redux/actions/groupActions";
import Button from "../components/shared/Button";
import TextInput from "../components/shared/TextInput";
import Card from "../components/shared/Card";
import Badge from "../components/shared/Badge";
import Modal from "../components/shared/Modal";
import ButtonLoadingSpinner from "../components/loader/ButtonLoadingSpinner";
import { cn } from "../utils/cn";
import { useIsAdmin } from "../hooks/useIsAdmin";

const GroupsManager = () => {
    const dispatch = useDispatch();
    const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);
    const isAdmin = useIsAdmin();
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [groupsRewardDistribution, setGroupsRewardDistribution] = useState({});
    const [expandedAction, setExpandedAction] = useState(null);
    const [deleteConfirmGroup, setDeleteConfirmGroup] = useState(null);
    const [isDeletingGroup, setIsDeletingGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [members, setMembers] = useState(currentOrganisation?.members ? currentOrganisation?.members : []);
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [availableMembers, setAvailableMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);
    const [addingMember, setAddingMember] = useState(null);
    const [removingMember, setRemovingMember] = useState(null);
    const [editGroupTarget, setEditGroupTarget] = useState(null);
    const [editGroupName, setEditGroupName] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => {
        if (currentOrganisation?.groups) {
            const distribution = {};
            currentOrganisation.groups.forEach(group => {
                if (group && group._id) {
                    distribution[group._id.toString()] = group.rewardDistribution * 100;
                }
            });
            setGroupsRewardDistribution(distribution);
        }
        setAvailableMembers(currentOrganisation ? currentOrganisation?.members : []);
    }, [currentOrganisation]);

    const toggleExpand = (groupId, action) => {
        setExpandedGroup(expandedGroup === groupId && expandedAction === action ? null : groupId);
        setExpandedAction(action);
    };

    const deleteGroup = async () => {
        const groupId = deleteConfirmGroup;
        if (!groupId) return;
        const target = currentOrganisation?.groups?.find((g) => g._id === groupId);
        if (target?.members?.length > 0) {
            alert("Cannot delete this group. Remove all members first.");
            setDeleteConfirmGroup(null);
            return;
        }
        setIsDeletingGroup(true);
        try {
            const result = await dispatch(deleteGroupAction(currentOrganisation._id, groupId));
            if (result?.error) {
                alert(result.error);
            }
            setDeleteConfirmGroup(null);
        } finally {
            setIsDeletingGroup(false);
        }
    };

    const createNewGroup = () => {
        if (!newGroupName.trim()) return;
        dispatch(createGroupAction({
            organisationId: currentOrganisation._id,
            name: newGroupName,
        })).then(() => {
            setNewGroupName("");
        });
    };

    const [copiedGroupId, setCopiedGroupId] = useState(null);

    const copyGroupId = (id) => {
        navigator.clipboard.writeText(id);
        setCopiedGroupId(id);
        setTimeout(() => setCopiedGroupId(null), 2000);
    };

    const [isDragging, setIsDragging] = useState(false);
    const [activeGroup, setActiveGroup] = useState(null);

    const handleMouseDown = (e, groupId) => {
        e.preventDefault();
        e.stopPropagation();

        const sliderTrack = document.getElementById(`slider-track-${groupId}`);
        if (!sliderTrack) return;

        setIsDragging(true);
        setActiveGroup(groupId);

        let currentDragValue = groupsRewardDistribution[groupId] || 0;

        const handleMove = (moveEvent) => {
            const clientX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
            if (!clientX) return;
            if (moveEvent.touches) moveEvent.preventDefault();

            const rect = sliderTrack.getBoundingClientRect();
            const offsetX = clientX - rect.left;
            const rawPercentage = (offsetX / rect.width) * 100;
            const percentage = Math.max(0, Math.min(100, rawPercentage));

            currentDragValue = percentage;

            setGroupsRewardDistribution(prev => ({
                ...prev,
                [groupId]: percentage
            }));
        };

        const handleEnd = () => {
            const roundedValue = Math.round(currentDragValue);

            setGroupsRewardDistribution(prev => ({
                ...prev,
                [groupId]: roundedValue
            }));

            dispatch(adjustRewardDistributionAction(
                currentOrganisation._id,
                groupId,
                roundedValue / 100
            ));

            setIsDragging(false);
            setActiveGroup(null);

            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('touchmove', handleMove);
            document.removeEventListener('mouseup', handleEnd);
            document.removeEventListener('touchend', handleEnd);
        };

        document.addEventListener('mousemove', handleMove, { passive: false });
        document.addEventListener('touchmove', handleMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchend', handleEnd);

        handleMove(e);
    };

    const handleMemberClick = (member) => {
        if (selectedMember && selectedMember._id === member._id) {
            const memberElement = document.getElementById(`member-${member._id}`);
            if (memberElement) {
                memberElement.classList.add('animate-collapse-member');
                setTimeout(() => {
                    setSelectedMember(null);
                }, 300);
            } else {
                setSelectedMember(null);
            }
        } else {
            setSelectedMember(member);
        }
    };

    const handleAddMember = (organisationId, groupId, memberId) => {
        setAddingMember(memberId);
        dispatch(addMembersAction(organisationId, groupId, [memberId]))
            .then(() => {
                setTimeout(() => {
                    setAddingMember(null);
                }, 800);
            });
    };

    const openEditGroup = (group) => {
        setEditGroupTarget(group);
        setEditGroupName(group?.name || "");
    };

    const closeEditGroup = () => {
        setEditGroupTarget(null);
        setEditGroupName("");
        setSavingEdit(false);
    };

    const submitEditGroup = () => {
        const trimmed = editGroupName.trim();
        if (!editGroupTarget || !trimmed || trimmed === editGroupTarget.name) {
            closeEditGroup();
            return;
        }
        setSavingEdit(true);
        dispatch(renameGroupAction(currentOrganisation._id, editGroupTarget._id, trimmed))
            .then(() => closeEditGroup());
    };

    const handleRemoveMember = (organisationId, groupId, memberId) => {
        setRemovingMember(memberId);
        dispatch(removeMembersAction(organisationId, groupId, [memberId]))
            .then(() => {
                setTimeout(() => {
                    setRemovingMember(null);
                }, 800);
            });
    };

    return (
        <div className="min-h-screen">
            {/* Header section */}
            <div className="bg-canvas border-b border-hairline">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6 gap-4 flex-wrap">
                        <h1 className="text-display-md text-ink" data-help-title="Groups" data-help="Groups let you organise members into segments and share a portion of every member's earned points with the group. Create groups, assign members, and set each group's reward split here.">Manage Groups</h1>
                        {isAdmin && (
                            <div className="flex items-center gap-3">
                                <div className="w-64">
                                    <TextInput
                                        type="text"
                                        placeholder="Enter group name..."
                                        data-help-title="New group name"
                                        data-help="Type a name for the new group here. Press Enter or click Add Group to create it. Admin only."
                                        value={newGroupName}
                                        onChange={(e) => setNewGroupName(e.target.value)}
                                        onKeyPress={(e) => e.key === "Enter" && createNewGroup()}
                                        trailingIcon={
                                            newGroupName ? (
                                                <button onClick={() => setNewGroupName("")}>
                                                    <HiX className="w-5 h-5" />
                                                </button>
                                            ) : null
                                        }
                                    />
                                </div>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={createNewGroup}
                                    leadingIcon={<HiPlus className="h-4 w-4" />}
                                    data-help-title="Add group"
                                    data-help="Creates a new group with the typed name. Groups organise members and control how reward points are split. Admin only."
                                >
                                    Add Group
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {currentOrganisation?.groups.length === 0 ? (
                    <Card variant="default" className="!p-16 text-center">
                        <div className="inline-flex items-center justify-center p-6 rounded-full bg-surface-soft border border-hairline mb-6">
                            <HiUsers className="text-5xl text-ink" />
                        </div>
                        <h3 className="text-title-md text-ink mb-2">No groups yet</h3>
                        <p className="text-body-md text-muted max-w-md mx-auto">
                            Create your first group using the form above to start organizing your members.
                        </p>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {currentOrganisation?.groups.map((group) => {
                            let ownerPercentage = "100%";
                            if (groupsRewardDistribution[group._id] > 0) {
                                ownerPercentage = `${Math.round((100 - groupsRewardDistribution[group._id]))}%`;
                            }
                            const groupPercentage = `${Math.round(groupsRewardDistribution[group._id])}%`;
                            const isExpanded = expandedGroup === group._id;

                            return (
                                <Fragment key={group._id}>
                                    <Card
                                        variant="default"
                                        className={cn(
                                            "transition-colors",
                                            isExpanded && "border-info bg-surface-soft"
                                        )}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                                            <div className="lg:col-span-3">
                                                <div className="text-label-md font-medium text-ink">{group.name}</div>
                                                {group._id && (
                                                <div className="flex items-center gap-1 mt-1">
                                                    <span className="text-caption text-muted font-mono bg-surface-soft px-1.5 py-0.5 rounded-sm">
                                                        {String(group._id).slice(-12)}
                                                    </span>
                                                    <button
                                                        onClick={() => copyGroupId(group._id)}
                                                        className="p-0.5 rounded-sm text-muted hover:text-ink transition-colors"
                                                        title="Copy Group ID"
                                                        data-help-title="Copy group ID"
                                                        data-help="Copies the group's full ID to the clipboard — useful when configuring integrations that need to reference this group. The chip shows only the last 12 characters."
                                                    >
                                                        {copiedGroupId === group._id
                                                            ? <CheckIcon className="h-3.5 w-3.5 text-signature-forest" />
                                                            : <ClipboardCopyIcon className="h-3.5 w-3.5" />
                                                        }
                                                    </button>
                                                </div>
                                                )}
                                            </div>

                                            {/* Points distribution */}
                                            <div className="lg:col-span-5">
                                                <div className="flex flex-col w-full gap-2">
                                                    <div className="flex justify-between text-body-md">
                                                        <span className="text-muted">
                                                            {group.name}:{" "}
                                                            <span className="font-medium text-signature-forest">{groupPercentage}</span>
                                                        </span>
                                                        <span className="text-muted">
                                                            Member:{" "}
                                                            <span className="font-medium text-ink">{ownerPercentage}</span>
                                                        </span>
                                                    </div>

                                                    <div className="relative w-full">
                                                        <div
                                                            id={`slider-track-${group._id}`}
                                                            className="w-full bg-surface-strong rounded-full h-2 overflow-hidden cursor-pointer"
                                                            data-help-title="Reward split"
                                                            data-help="Sets what share of earned reward points goes to the group's pool; the remainder stays with the individual member. Click anywhere on the bar to set the split — the change is saved automatically. Admin only."
                                                            onClick={(e) => {
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                const clickX = e.clientX - rect.left;
                                                                const percentage = (clickX / rect.width) * 100;
                                                                const newValue = Math.max(0, Math.min(100, percentage));

                                                                setGroupsRewardDistribution(prevState => ({
                                                                    ...prevState,
                                                                    [group._id]: newValue
                                                                }));

                                                                dispatch(adjustRewardDistributionAction(
                                                                    currentOrganisation._id,
                                                                    group._id,
                                                                    Math.round(newValue) / 100
                                                                ));
                                                            }}
                                                        >
                                                            <div
                                                                className="h-2 rounded-full bg-ink transition-transform duration-75 ease-out"
                                                                style={{
                                                                    width: `${groupsRewardDistribution[group._id]}%`,
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Drag handle (replaces heart) */}
                                                        <div
                                                            className="absolute top-0 transform -translate-y-1/2 w-5 h-5 cursor-grab active:cursor-grabbing transition-transform duration-75 z-20"
                                                            style={{
                                                                left: `${groupsRewardDistribution[group._id]}%`,
                                                                transform: `translateX(-50%) translateY(-50%)`,
                                                            }}
                                                            onMouseDown={(e) => handleMouseDown(e, group._id)}
                                                            onTouchStart={(e) => handleMouseDown(e, group._id)}
                                                            data-help-title="Drag handle"
                                                            data-help="Drag to adjust how earned points are split between the group and the member. The rounded percentage is saved when you release. Admin only."
                                                        >
                                                            <div
                                                                className={cn(
                                                                    "w-5 h-5 rounded-full border-2 border-canvas shadow-card",
                                                                    isDragging && activeGroup === group._id
                                                                        ? "bg-signature-coral"
                                                                        : "bg-ink"
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="lg:col-span-2 flex justify-center gap-1">
                                                {group._id && (
                                                    <Link
                                                        to={`/members/groups/${group._id}`}
                                                        className="p-2 rounded-md text-muted hover:text-ink hover:bg-surface-soft transition-colors"
                                                        aria-label={`View details for ${group.name}`}
                                                        title="View details"
                                                        data-help-title="Group details"
                                                        data-help="Opens this group's detail page showing its member count, points balance, reward share, and revenue history."
                                                    >
                                                        <HiOutlineChartBar className="w-5 h-5" />
                                                    </Link>
                                                )}
                                                {isAdmin && (
                                                    <>
                                                        <button
                                                            className="p-2 rounded-md text-muted hover:text-ink hover:bg-surface-soft transition-colors"
                                                            onClick={() => openEditGroup(group)}
                                                            aria-label={`Rename ${group.name}`}
                                                            data-help-title="Rename group"
                                                            data-help="Opens a dialog to rename this group. Admin only."
                                                        >
                                                            <HiPencilAlt className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            className="p-2 rounded-md text-muted hover:text-signature-coral hover:bg-surface-soft transition-colors"
                                                            onClick={() => setDeleteConfirmGroup(group._id)}
                                                            data-help-title="Delete group"
                                                            data-help="Opens a confirmation to permanently delete this group. Deletion cannot be undone and is blocked while the group still has members. Admin only."
                                                        >
                                                            <HiTrash className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            className={cn(
                                                                "p-2 rounded-md transition-colors",
                                                                isExpanded && expandedAction === "add"
                                                                    ? "bg-ink text-onInk"
                                                                    : "text-muted hover:text-ink hover:bg-surface-soft"
                                                            )}
                                                            onClick={() => toggleExpand(group._id, "add")}
                                                            data-help-title="Add members"
                                                            data-help="Expands a panel where you can search organisation members and add them to this group. Admin only."
                                                        >
                                                            <HiPlus className="w-5 h-5" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>

                                            {/* Members */}
                                            <div className="lg:col-span-2 text-center">
                                                <Button
                                                    variant={
                                                        isExpanded && expandedAction === "show" ? "primary" : "secondary"
                                                    }
                                                    size="sm"
                                                    onClick={() => toggleExpand(group._id, "show")}
                                                    data-help-title="View members"
                                                    data-help="Shows or hides the list of members currently in this group. The label shows the member count."
                                                    leadingIcon={
                                                        isExpanded && expandedAction === "show" ? (
                                                            <HiChevronUp className="w-4 h-4" />
                                                        ) : (
                                                            <HiUsers className="w-4 h-4" />
                                                        )
                                                    }
                                                >
                                                    {isExpanded && expandedAction === "show"
                                                        ? "Hide Members"
                                                        : `${group.members?.length || 0} Members`}
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Expanded Section */}
                                        {isExpanded && (
                                            <div className="mt-6 pt-6 border-t border-hairline">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-title-sm text-ink">
                                                        {expandedAction === "add"
                                                            ? `Add Members to ${group.name}`
                                                            : `Members in ${group.name}`}
                                                    </h3>
                                                    <button
                                                        onClick={() => toggleExpand(null, null)}
                                                        className="text-muted hover:text-ink transition-colors p-1 rounded-md"
                                                    >
                                                        <HiX className="w-5 h-5" />
                                                    </button>
                                                </div>

                                                <div className="mb-4">
                                                    <TextInput
                                                        type="text"
                                                        placeholder={
                                                            expandedAction === "add"
                                                                ? "Search members to add..."
                                                                : "Filter current members..."
                                                        }
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        leadingIcon={<HiSearch className="w-5 h-5" />}
                                                        data-help-title="Member search"
                                                        data-help="Filters the member list by name or identification — either when picking organisation members to add, or when filtering the group's current members."
                                                    />
                                                </div>

                                                {isLoadingMembers ? (
                                                    <div className="flex flex-col items-center justify-center p-12">
                                                        <div className="w-12 h-12 border-4 border-hairline border-t-ink rounded-full animate-spin mb-4" />
                                                        <p className="text-body-md text-muted">Loading members...</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {(expandedAction === "add"
                                                            ? availableMembers.filter((member) =>
                                                                  member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                                  member.identification?.toLowerCase().includes(searchQuery.toLowerCase())
                                                              )
                                                            : (availableMembers?.filter((member) => group.members?.includes(member._id)) || [])
                                                                  .filter((m) =>
                                                                      (m.name || m.identification)?.toLowerCase().includes(searchQuery.toLowerCase())
                                                                  )
                                                        ).length === 0 ? (
                                                            <div className="text-center py-12 px-4 bg-canvas rounded-md border border-hairline">
                                                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-surface-soft mb-4">
                                                                    <HiUsers className="w-8 h-8 text-muted" />
                                                                </div>
                                                                <h4 className="text-title-sm text-ink mb-1">No members found</h4>
                                                                <p className="text-body-md text-muted">
                                                                    {expandedAction === "add"
                                                                        ? "Try adjusting your search to find members to add."
                                                                        : "This group doesn't have any members yet."}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                {(expandedAction === "add"
                                                                    ? availableMembers.filter((member) =>
                                                                          member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                                          member.identification?.toLowerCase().includes(searchQuery.toLowerCase())
                                                                      )
                                                                    : (availableMembers?.filter((member) => group.members?.includes(member._id)) || [])
                                                                          .filter((m) =>
                                                                              (m.name || m.identification)?.toLowerCase().includes(searchQuery.toLowerCase())
                                                                          )
                                                                ).map((member) => {
                                                                    const isSelected = selectedMember && selectedMember._id === member._id;
                                                                    const isAdding = addingMember === member._id;
                                                                    const isRemoving = removingMember === member._id;

                                                                    return (
                                                                        <Card
                                                                            key={member._id}
                                                                            id={`member-${member._id}`}
                                                                            variant="default"
                                                                            className={cn(
                                                                                "transition-all cursor-pointer !p-4",
                                                                                isSelected && "border-info lg:col-span-3 md:col-span-2"
                                                                            )}
                                                                            onClick={() => handleMemberClick(member)}
                                                                            data-help-title="Member card"
                                                                            data-help="Click to expand this member's details — email, role, and join date. Click again to collapse."
                                                                        >
                                                                            <div className="flex justify-between items-center">
                                                                                <div className="flex items-center">
                                                                                    <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 font-medium text-ink bg-surface-soft border border-hairline">
                                                                                        {(member.name ? member.name[0] : member.identification[0]).toUpperCase()}
                                                                                    </div>
                                                                                    <div>
                                                                                        <p className="font-medium text-ink">
                                                                                            {member.name || member.identification}
                                                                                        </p>
                                                                                        <p className="text-body-md text-muted">
                                                                                            Member ID: {member._id.slice(-6)}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>

                                                                                {expandedAction === "add" ? (
                                                                                    group.members?.includes(member._id) ? (
                                                                                        <Badge variant="success">Added</Badge>
                                                                                    ) : (
                                                                                        <Button
                                                                                            variant="primary"
                                                                                            size="sm"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleAddMember(currentOrganisation._id, group._id, member._id);
                                                                                            }}
                                                                                            leadingIcon={<HiPlus className="w-4 h-4" />}
                                                                                            data-help-title="Add member"
                                                                                            data-help="Puts this member into the group; the badge changes to Added to confirm membership. Admin only."
                                                                                        >
                                                                                            {isAdding ? "Adding..." : "Add"}
                                                                                        </Button>
                                                                                    )
                                                                                ) : isAdmin ? (
                                                                                    <Button
                                                                                        variant="danger"
                                                                                        size="sm"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleRemoveMember(currentOrganisation._id, group._id, member._id);
                                                                                        }}
                                                                                        leadingIcon={<HiX className="w-4 h-4" />}
                                                                                        data-help-title="Remove member"
                                                                                        data-help="Takes this member out of the group. Admin only."
                                                                                    >
                                                                                        {isRemoving ? "Removing..." : "Remove"}
                                                                                    </Button>
                                                                                ) : null}
                                                                            </div>

                                                                            {isSelected && (
                                                                                <div className="mt-4 pt-4 border-t border-hairline">
                                                                                    <h4 className="text-body-md font-medium text-ink mb-2">Member Details</h4>
                                                                                    <div className="space-y-2">
                                                                                        <div className="flex justify-between text-body-md">
                                                                                            <span className="text-muted">Email:</span>
                                                                                            <span className="text-ink">{member.email || "Not available"}</span>
                                                                                        </div>
                                                                                        <div className="flex justify-between text-body-md">
                                                                                            <span className="text-muted">Role:</span>
                                                                                            <span className="text-ink">{member.role || "Member"}</span>
                                                                                        </div>
                                                                                        <div className="flex justify-between text-body-md">
                                                                                            <span className="text-muted">Joined:</span>
                                                                                            <span className="text-ink">{member.joinedDate || "Unknown"}</span>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </Card>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </Card>
                                </Fragment>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Edit Group Modal */}
            <Modal
                isOpen={!!editGroupTarget}
                onClose={closeEditGroup}
                title="Rename Group"
                size="sm"
                footer={
                    <>
                        <Button variant="secondary" size="sm" onClick={closeEditGroup} disabled={savingEdit}>
                            Cancel
                        </Button>
                        <Button variant="primary" size="sm" onClick={submitEditGroup} disabled={savingEdit || !editGroupName.trim()}>
                            {savingEdit ? "Saving..." : "Save"}
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <label className="block text-body-md text-muted">Group name</label>
                    <TextInput
                        type="text"
                        value={editGroupName}
                        onChange={(e) => setEditGroupName(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && submitEditGroup()}
                        autoFocus
                    />
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={!!deleteConfirmGroup}
                onClose={() => { if (!isDeletingGroup) setDeleteConfirmGroup(null); }}
                title="Delete Group"
                size="sm"
                footer={
                    <>
                        <Button variant="secondary" size="sm" onClick={() => setDeleteConfirmGroup(null)} disabled={isDeletingGroup}>
                            Cancel
                        </Button>
                        <Button variant="danger" size="sm" onClick={deleteGroup} disabled={isDeletingGroup}>
                            {isDeletingGroup ? <ButtonLoadingSpinner loadingText="Deleting..." /> : "Delete Group"}
                        </Button>
                    </>
                }
            >
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-signature-coral/10 rounded-full mb-4">
                        <HiTrash className="w-6 h-6 text-signature-coral" />
                    </div>
                    <p className="text-body-md text-body">
                        This action cannot be undone. All group settings will be permanently deleted.
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default GroupsManager;
